import { uniqueId, without } from 'lodash';

// @ts-expect-error - RichSlate does not have types yet
import { convertHtmlContentToPlainText, htmlToSlate, slateToText } from 'src/components/RichSlate';
import { type AssetType, isImageAssetType } from 'src/data/asset';
import {
  GENERATION_IMPROVE_MODE,
  type GenerationImproveMode,
  type ImproveParams,
} from 'src/data/generation';
import {
  GENERATION_TOOL,
  type GenerationTool,
  getGenerationToolForAssetType,
  isBasicContentImproverTool,
  isImageGenerationTool,
  shouldDisableTOVModes,
} from 'src/data/generationTool';
import { mimicImageResource } from 'src/data/performance';
import {
  SHORT_FORM_CONTENT_TYPE,
  type ShortFormContentType,
  shortFormContentTypeHasCSVInput,
  shortFormContentTypeHasScreenshotInput,
  type ShortFormSection,
} from 'src/data/shortForm';
import { isSlateImageNode, type SlateValue } from 'src/lib/slate';

export const IMPROVER_CONTENT_INPUT_MODE = {
  screenshot: 'screenshot',
  csv: 'csv',
  sections: 'sections',
};
export type ImproverContentInputMode =
  (typeof IMPROVER_CONTENT_INPUT_MODE)[keyof typeof IMPROVER_CONTENT_INPUT_MODE];

// Tools that should not have "make longer"/"make shorter" options
const TOOLS_WITHOUT_LENGTH_MODES: GenerationTool[] = [
  GENERATION_TOOL.googleRSA,
  GENERATION_TOOL.googlePerformanceMaxAd,
  GENERATION_TOOL.googleHeadlineProduct,
  GENERATION_TOOL.googleLongHeadlineProduct,
  GENERATION_TOOL.googleDescriptionProduct,

  GENERATION_TOOL.linkedinConversationalAdPrimaryCtaButtonText,
  GENERATION_TOOL.linkedinConversationalAdSecondaryCtaButtonText,
  GENERATION_TOOL.linkedinMessageAdCta,
];

export const contentInputModeFromContentType = (
  contentType: ShortFormContentType
): ImproverContentInputMode => {
  if (shortFormContentTypeHasCSVInput(contentType)) {
    return IMPROVER_CONTENT_INPUT_MODE.csv;
  }
  if (shortFormContentTypeHasScreenshotInput(contentType)) {
    return IMPROVER_CONTENT_INPUT_MODE.screenshot;
  }
  return IMPROVER_CONTENT_INPUT_MODE.sections;
};

export const isContentTypeClearable = (contentType: ShortFormContentType): boolean => {
  return (
    shortFormContentTypeHasCSVInput(contentType) ||
    shortFormContentTypeHasScreenshotInput(contentType)
  );
};

export const isRewriteSectionEmpty = (section: ShortFormSection) => {
  if (isImageGenerationTool(section.generationTool)) {
    return !section.imageResource;
  }

  const textContent = convertHtmlContentToPlainText(section.content || '');
  return !textContent.trim();
};

export const isRewriteSectionSupportHtml = (
  contentType: ShortFormContentType,
  section: ShortFormSection
) => {
  if (contentType === SHORT_FORM_CONTENT_TYPE.generalText) {
    return true;
  }

  if (
    contentType === SHORT_FORM_CONTENT_TYPE.email &&
    section.generationTool === GENERATION_TOOL.emailBody
  ) {
    return true;
  }

  return false;
};

export const TEXT_IMPROVER_MODES: GenerationImproveMode[] = [
  GENERATION_IMPROVE_MODE.boostScoreEngaging,
  GENERATION_IMPROVE_MODE.boostScoreSimilar,
  GENERATION_IMPROVE_MODE.makeCasual,
  GENERATION_IMPROVE_MODE.makeLonger,
  GENERATION_IMPROVE_MODE.makeShorter,
  GENERATION_IMPROVE_MODE.changeTov,
  GENERATION_IMPROVE_MODE.sfv,
  GENERATION_IMPROVE_MODE.formatGroups,
  GENERATION_IMPROVE_MODE.instructions,
];

export const IMAGE_IMPROVER_MODES: GenerationImproveMode[] = [
  // GENERATION_IMPROVE_MODE.improveImage, // TODO: Uncomment when "image" performance improvement is supported by BE/AC
  GENERATION_IMPROVE_MODE.instructions,
  GENERATION_IMPROVE_MODE.changeImageStyle,
];

export const getImproveModesForTool = (generationTool: GenerationTool): GenerationImproveMode[] => {
  if (isImageGenerationTool(generationTool)) {
    return IMAGE_IMPROVER_MODES;
  }

  let textModes = TEXT_IMPROVER_MODES;

  // "basic content improver" should not include "boost score" modes
  if (isBasicContentImproverTool(generationTool)) {
    const BASIC_IMPROVE_MODES = [
      GENERATION_IMPROVE_MODE.improveEngaging,
      GENERATION_IMPROVE_MODE.improveSimilar,
    ];

    const BASIC_IMPROVE_EXCLUDED_MODES = [
      GENERATION_IMPROVE_MODE.boostScoreEngaging,
      GENERATION_IMPROVE_MODE.boostScoreSimilar,
    ];

    // replace "boost score" modes with "improve" modes
    textModes = [...BASIC_IMPROVE_MODES, ...without(textModes, ...BASIC_IMPROVE_EXCLUDED_MODES)];
  }

  if (shouldDisableTOVModes(generationTool)) {
    textModes = without(textModes, GENERATION_IMPROVE_MODE.changeTov);
  }

  // "Google" and some "LinkedIn" types should not have "longer"/"shorter" mode
  if (TOOLS_WITHOUT_LENGTH_MODES.includes(generationTool)) {
    textModes = without(
      textModes,
      GENERATION_IMPROVE_MODE.makeLonger,
      GENERATION_IMPROVE_MODE.makeShorter
    );
  }

  return textModes;
};

export const getSectionImproveModesForTool = (
  generationTool: GenerationTool
): GenerationImproveMode[] => {
  const improveModes = getImproveModesForTool(generationTool);

  if (isImageGenerationTool(generationTool)) {
    return [GENERATION_IMPROVE_MODE.doNotImprove, ...improveModes];
  }

  // "text" section in content improver has a separate button to change SFV options
  return [
    GENERATION_IMPROVE_MODE.doNotImprove,
    ...without(improveModes, GENERATION_IMPROVE_MODE.sfv),
  ];
};

const generateLocalSectionId = (): string => uniqueId('local_section_');

/**
 * Flushes accumulated text nodes into a text section if there's meaningful content.
 */
const flushTextSection = (
  mainSectionTool: GenerationTool,
  sections: ShortFormSection[],
  accumulatedNodes: SlateValue,
  textSectionCounter: { value: number }
): void => {
  if (accumulatedNodes.length === 0) {
    return;
  }

  const textContent = slateToText(accumulatedNodes)?.trim();
  if (textContent) {
    sections.push({
      id: generateLocalSectionId(),
      name: `Text ${textSectionCounter.value}`,
      generationTool: mainSectionTool,
      content: textContent,
    });
    textSectionCounter.value++;
  }
};

/**
 * Splits HTML content into subsequent text and image sections.
 * Text sections contain plain text (extracted from HTML), image sections contain image URLs.
 */
export const splitHtmlToRewriteSections = (
  sectionTool: GenerationTool,
  contentHtml: string
): ShortFormSection[] => {
  if (!contentHtml || !contentHtml.trim()) {
    return [];
  }

  const slateValue: SlateValue = htmlToSlate(contentHtml);
  const sections: ShortFormSection[] = [];
  const textSectionCounter = { value: 1 };
  let imageSectionCounter = 1;
  let accumulatedNodes: SlateValue = [];

  for (const node of slateValue) {
    if (isSlateImageNode(node)) {
      // Flush any accumulated text nodes before adding image section
      flushTextSection(sectionTool, sections, accumulatedNodes, textSectionCounter);
      accumulatedNodes = [];

      // Add image section
      const imageUrl = node.src || '';
      if (imageUrl) {
        sections.push({
          id: generateLocalSectionId(),
          name: `Image ${imageSectionCounter}`,
          generationTool: GENERATION_TOOL.imageImprove,
          content: '',
          imageResource: mimicImageResource({ path: imageUrl }),
          improveParams: {
            improveMode: GENERATION_IMPROVE_MODE.doNotImprove,
          },
        });
        imageSectionCounter++;
      }
    } else {
      // Accumulate non-image nodes
      accumulatedNodes.push(node);
    }
  }

  // Flush any remaining accumulated text nodes
  flushTextSection(sectionTool, sections, accumulatedNodes, textSectionCounter);

  return sections;
};

/**
 * Merges a list of ShortFormSection items back into an HTML string.
 * Text sections are wrapped in <p> tags, image sections become <img> tags.
 */
export const mergeRewriteSectionsIntoHtml = (sections: ShortFormSection[]): string => {
  if (!sections || sections.length === 0) {
    return '';
  }

  return sections
    .map((section) => {
      if (isImageGenerationTool(section.generationTool)) {
        const imageUrl = section.imageResource?.file?.originalUrl || '';
        return imageUrl ? `<img src="${imageUrl}" />` : '';
      }
      const content = section.content || '';
      return content ? `<p>${content}</p>` : '';
    })
    .join('');
};

export const prepareSectionImproveParams = (
  section: ShortFormSection,
  defaultParams?: ImproveParams
) => {
  // if section has its own improve params, use them
  if (section.improveParams?.improveMode) {
    return section.improveParams;
  }

  // see if we can fallback to default improve params for a section
  if (defaultParams) {
    // default params can be used for a section if:
    // - section is not an image asset
    // - or default mode is "change image style" (we check for mode specifically, because we don't want to mix "instructions" between text and image sections)
    const canUseTopLevelParams =
      !isImageGenerationTool(section.generationTool) ||
      defaultParams.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle;

    if (canUseTopLevelParams) {
      const improveModes = getSectionImproveModesForTool(section.generationTool);
      if (improveModes.includes(defaultParams.improveMode)) {
        return defaultParams;
      }
    }
  }

  return undefined;
};

export const getSectionToolFromAssetType = (assetType: AssetType): GenerationTool | undefined => {
  if (isImageAssetType(assetType)) {
    return GENERATION_TOOL.imageImprove;
  }

  return getGenerationToolForAssetType(assetType) || undefined;
};
